import { useCallback, useContext, useReducer } from 'react';
import { RECAPTCHA_SUBSCRIBE } from '@lemonadefashion/recaptcha-actions';
import { getApiClient, makeStandardApiErrorHandler } from '../../api_client/get';
import { validateEmail, validatePersonName } from '@lemonadefashion/validators';
import RecaptchaContext from '../../contexts/recaptcha';
import CartContext from '../../contexts/cart';

const reducer = (state, action) => {
  switch (action.type) {
    case 'validation-error': return { ...state, errorMessage: action.message, errorCounter: state.errorCounter + 1 };
    case 'change-name': return { ...state, name: action.value, errorMessage: null };
    case 'change-email': return { ...state, email: action.value, errorMessage: null };
    case 'change-subscribe-gender-interest':
      return { ...state, subscribeGenderInterest: action.value, errorMessage: null };
    case 'start-subscribe': return { ...state, errorMessage: null, waiting: true };
    case 'subscribe-success': return { ...state, waiting: false, finished: true };
    case 'subscribe-error':
      return { ...state, waiting: false, errorMessage: action.message, errorCounter: state.errorCounter + 1 };
    default: throw new Error('Unhandled action: ' + action.type);
  }
};

const useSubscribeBox = (noName, promotion, onSubscribe) => {
  const [state, dispatch] = useReducer(reducer, {
    name: '',
    email: '',
    subscribeGenderInterest: 'female',
    errorMessage: null,
    errorCounter: 0,
    waiting: false,
    finished: false
  });
  const recaptcha = useContext(RecaptchaContext);
  const { name, email, subscribeGenderInterest, errorMessage, errorCounter, waiting, finished } = state;
  const cart = useContext(CartContext);

  const validateAndSubmit = useCallback(() => {
    if (waiting || finished) return;
    const emailValidation = validateEmail(email, 'Email');
    if (!emailValidation.success) {
      dispatch({ type: 'validation-error', message: emailValidation.message });
      return;
    }
    if (!noName) {
      const nameValidation = validatePersonName(name, 'Name');
      if (!nameValidation.success) {
        dispatch({ type: 'validation-error', message: nameValidation.message });
        return;
      }
    }
    if (subscribeGenderInterest !== 'male' && subscribeGenderInterest !== 'female') {
      dispatch({ type: 'validation-error', message: 'Please choose your interest' });
      return;
    }
    dispatch({ type: 'start-subscribe' });
    recaptcha.executeWithRetries(RECAPTCHA_SUBSCRIBE)
      .then(recaptchaToken => {
        const nameToSend = noName ? undefined : name;
        const promotionToSend = promotion || undefined;
        getApiClient().subscribe(email, nameToSend, subscribeGenderInterest, promotionToSend, recaptchaToken)
          .then(response => {
            if (response.data.success) {
              dispatch({ type: 'subscribe-success' });
              cart.setElementsAndPricing(response.data.cart, response.data.cart_pricing);
              if (onSubscribe) onSubscribe();
            } else {
              dispatch({ type: 'subscribe-error', message: response.data.message });
            }
          })
          .catch(makeStandardApiErrorHandler(msg => dispatch({ type: 'subscribe-error', message: msg })));
      })
      .catch(error => dispatch({ type: 'subscribe-error', message: error.message }));
  }, [name, email, subscribeGenderInterest, noName, promotion, recaptcha, onSubscribe, waiting, finished, cart]);

  const onChangeName = useCallback(e => dispatch({ type: 'change-name', value: e.target.value }), []);
  const onChangeEmail = useCallback(e => dispatch({ type: 'change-email', value: e.target.value }), []);
  const onChangeSubscribeGenderInterest = useCallback((_, { value }) => {
    dispatch({ type: 'change-subscribe-gender-interest', value: value });
  }, []);
  const onClickSubscribeGenderInterest = useCallback((genderValue) => {
    console.log(genderValue);
    dispatch({ type: 'change-subscribe-gender-interest', value: genderValue });
  }, []);

  return {
    name,
    email,
    subscribeGenderInterest,
    waiting,
    finished,
    errorMessage,
    errorCounter,
    validateAndSubmit,
    onChangeName,
    onChangeEmail,
    onChangeSubscribeGenderInterest,
    onClickSubscribeGenderInterest
  };
};

export default useSubscribeBox;
